import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDefinitionResource } from 'src/components/star-grid/hooks/useDefinitionResource';
import {
  checkDatasetAccess,
  createDataset,
  getDataset,
  getDatasetColumns,
  getDatasetSources,
  getReportingScenarios,
  listHierarchies,
  previewDraftDataset,
  profileDataset,
  publishDataset,
  suggestLookupCandidates,
  updateDataset
} from 'src/components/star-grid/datasets/services/datasetService';
import type {
  AccessCheckResponse,
  ColumnRole,
  CalculatedMeasure,
  Dataset,
  DatasetCapabilities,
  DatasetColumnHierarchy,
  DatasetColumnInfo,
  DatasetFixedFilter,
  DatasetLookupCandidate,
  DatasetPreset,
  DatasetPreviewResponse,
  DatasetProfile,
  DatasetSource,
  ProfileColumn
} from 'src/components/star-grid/datasets/datasetTypes';
import { buildDatasetDescription, effectiveRole, savedFixedFilter } from 'src/components/star-grid/datasets/utils/datasetDraft';
import { suggestedScenarioMap } from 'src/components/star-grid/datasets/utils/datasetCapabilities';
import { mapRow, scenarioRows, type ScenarioRow } from 'src/components/star-grid/datasets/utils/scenarioRows';
import { reviewPreviewRequest } from 'src/components/star-grid/datasets/utils/datasetPreviewRequest';
import type { HierarchyChoice } from 'src/components/star-grid/datasets/utils/datasetHierarchy';
import { useLookupSelections } from 'src/components/star-grid/datasets/hooks/useLookupSelections';
import { useCalculatedMeasures } from 'src/components/star-grid/datasets/hooks/useCalculatedMeasures';
import { useScenarioSuggestions } from 'src/components/star-grid/datasets/hooks/useScenarioSuggestions';
import { canAdvance, isLastStep, stepIndex, wizardSteps, type WizardStepId } from 'src/components/star-grid/datasets/utils/wizardSteps';
import type { SourceMode } from 'src/components/star-grid/datasets/utils/sourceFilter';
import { errorMessage } from 'src/components/star-grid/utils/errorMessage';

export interface DatasetWizardOptions {
  readonly onSaved?: (dataset: Dataset) => void;
  /** When set, the wizard edits this dataset instead of onboarding a new one. */
  readonly editingId?: string;
}

function seedHierarchyChoices(
  columns: readonly { name: string; role: ColumnRole; hierarchy?: DatasetColumnHierarchy | null }[]
): Record<string, HierarchyChoice> {
  const choices: Record<string, HierarchyChoice> = {};
  columns
    .filter((column) => column.role === 'dimension')
    .forEach((column) => {
      choices[column.name] = column.hierarchy
        ? { kind: 'hierarchy', dimension: column.hierarchy.dimension, hierarchy: column.hierarchy.hierarchy, coverage: null }
        : { kind: 'flat' };
    });
  return choices;
}

/** Quiet time after a Review edit before the draft preview is re-read. */
const DRAFT_PREVIEW_DEBOUNCE_MS = 400;

/** The dataset-onboarding (and edit) wizard state machine: step navigation plus the network calls each entry runs. */
export function useDatasetWizard(options: DatasetWizardOptions = {}) {
  const { onSaved, editingId } = options;
  const editing = Boolean(editingId);
  const steps = useMemo(() => wizardSteps(editing), [editing]);

  const [step, setStep] = useState<WizardStepId>(editing ? 'map' : 'source');
  const [search, setSearchValue] = useState('');
  const [sourcePage, setSourcePage] = useState(1);
  const setSearch = useCallback((value: string) => {
    setSearchValue(value);
    setSourcePage(1);
  }, []);
  const [sourceMode, setSourceMode] = useState<SourceMode>('catalog');
  const [uriDraft, setUriDraftValue] = useState('');
  const [roleDraft, setRoleDraftValue] = useState('');
  // The server's own reason a typed path was refused, shown beside the path input.
  const [uriError, setUriError] = useState<string | null>(null);
  const [uriChecking, setUriChecking] = useState(false);
  // The role the selected source is read with; empty reads with StarGrid's own role.
  const [roleArn, setRoleArn] = useState('');
  const [sourceRefreshes, setSourceRefreshes] = useState(0);
  const [selectedRef, setSelectedRef] = useState<string | null>(null);
  const [fixedFilters, setFixedFilters] = useState<DatasetFixedFilter[]>([]);
  const [access, setAccess] = useState<AccessCheckResponse | null>(editing ? { accessible: true } : null);
  const [accessChecking, setAccessChecking] = useState(false);
  const [profile, setProfile] = useState<DatasetProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(editing);
  const [roles, setRoles] = useState<Record<string, ColumnRole>>({});
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [hierarchyChoices, setHierarchyChoices] = useState<Record<string, HierarchyChoice>>({});
  const [measureDimension, setMeasureDimension] = useState<string | null>(null);
  const [capabilities, setCapabilities] = useState<DatasetCapabilities>({});
  const [reachedMap, setReachedMap] = useState(editing);
  const lookupSelection = useLookupSelections();
  const { lookups, seed: seedLookups } = lookupSelection;
  const calculated = useCalculatedMeasures();
  const { seed: seedCalculated } = calculated;
  const [lookupCandidates, setLookupCandidates] = useState<DatasetLookupCandidate[]>([]);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<Dataset | null>(null);
  const [reviewPreview, setReviewPreview] = useState<DatasetPreviewResponse | null>(null);
  const [reviewPreviewLoading, setReviewPreviewLoading] = useState(false);
  const [reviewPreviewError, setReviewPreviewError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);

  const stepController = useRef<AbortController | null>(null);
  // Profiling a large table takes tens of seconds, so it gets its own controller (the access check and
  // save cannot strand it) and each (table, fixed filters) is profiled once per wizard.
  const profileController = useRef<AbortController | null>(null);
  const publishController = useRef<AbortController | null>(null);
  const profileInFlight = useRef<string | null>(null);
  const profiles = useRef(new Map<string, DatasetProfile>());
  const fetchedLookupDimensions = useRef(new Set<string>());

  // Onboarding fetches the whole catalog once and filters client-side; editing skips the source step.
  const sourcesFetcher = useCallback(
    async (signal: AbortSignal): Promise<DatasetSource[]> =>
      editing ? [] : (await getDatasetSources(undefined, sourceRefreshes > 0, signal)).sources,
    [editing, sourceRefreshes]
  );
  const sources = useDefinitionResource(sourcesFetcher, { keepPrevious: true });
  const refreshSources = useCallback(() => setSourceRefreshes((count) => count + 1), []);

  // Editing prefills every choice from the stored dataset, then re-profiles its table for column metadata.
  useEffect(() => {
    if (!editingId) return;
    const controller = new AbortController();
    setProfileLoading(true);
    setError(null);
    getDataset(editingId, undefined, controller.signal)
      .then((dataset) => {
        if (controller.signal.aborted) return undefined;
        setSelectedRef(dataset.ref);
        setRoleArn(dataset.role_arn ?? '');
        setName(dataset.name);
        setRoles(Object.fromEntries(dataset.columns.map((column) => [column.name, column.role])));
        setLabels(Object.fromEntries(dataset.columns.filter((column) => column.label).map((column) => [column.name, column.label as string])));
        setHierarchyChoices(seedHierarchyChoices(dataset.columns));
        setMeasureDimension(dataset.columns.find((column) => column.measure_dimension)?.name ?? null);
        seedLookups(dataset.lookups);
        seedCalculated(dataset.columns.filter((column) => column.formula).map(storedCalculatedMeasure));
        const filters = dataset.fixed_filters ?? [];
        setFixedFilters([...filters]);
        setCapabilities(dataset.capabilities ?? {});
        return profileDataset(dataset.ref, controller.signal, filters, dataset.role_arn || undefined);
      })
      .then((profiled) => {
        if (profiled && !controller.signal.aborted) setProfile(profiled);
      })
      .catch((caught: unknown) => {
        if (!controller.signal.aborted) setError(errorMessage(caught));
      })
      .finally(() => {
        if (!controller.signal.aborted) setProfileLoading(false);
      });
    return () => controller.abort();
  }, [editingId, seedLookups, seedCalculated]);

  const sourceRole = roleArn.trim() || undefined;

  // Onboarding only: an edit keeps its fixed filters, so it never needs the column list.
  const sourceColumnsFetcher = useCallback(
    (signal: AbortSignal): Promise<DatasetColumnInfo[]> =>
      editing || !selectedRef
        ? Promise.resolve([])
        : getDatasetColumns(selectedRef, signal, sourceRole).then(
            (response) => response.columns,
            () => []
          ),
    [editing, selectedRef, sourceRole]
  );
  const sourceColumns = useDefinitionResource(sourceColumnsFetcher);

  const appliedFilters = useMemo(() => fixedFilters.map(savedFixedFilter), [fixedFilters]);

  // The profile, coverage and lookup suggestions are all scoped to the fixed filters, so changing a
  // filter swaps in that slice's profile (if already read). An edit seeds its filters once and never changes them.
  useEffect(() => {
    if (editing) return;
    setProfile(selectedRef ? profiles.current.get(profileKey(selectedRef, appliedFilters, sourceRole)) ?? null : null);
    setLookupCandidates([]);
    fetchedLookupDimensions.current.clear();
  }, [editing, selectedRef, appliedFilters, sourceRole]);

  useEffect(
    () => () => [stepController, profileController, publishController, typedController].forEach((controller) => controller.current?.abort()),
    []
  );

  const addFixedFilter = useCallback(() => setFixedFilters((prev) => [...prev, { column: '', op: 'eq', values: [''] }]), []);
  const updateFixedFilter = useCallback(
    (index: number, filter: DatasetFixedFilter) => setFixedFilters((prev) => prev.map((existing, at) => (at === index ? filter : existing))),
    []
  );
  const removeFixedFilter = useCallback((index: number) => setFixedFilters((prev) => prev.filter((_, at) => at !== index)), []);

  // A typed path's access check has its own controller, so a catalog pick (or a newer check) cancels it.
  const typedController = useRef<AbortController | null>(null);
  // The table whose preset was applied; a new pick (even of the same table) clears it, so the preset fills again.
  const presetAppliedFor = useRef<string | null>(null);
  const applySource = useCallback(
    (ref: string, role = '') => {
      presetAppliedFor.current = null;
      setSelectedRef(ref);
      setRoleArn(role);
      setFixedFilters([]);
      setAccess(null);
      setProfile(null);
      setRoles({});
      setLabels({});
      setHierarchyChoices({});
      setMeasureDimension(null);
      setCapabilities({});
      seedLookups([]);
      seedCalculated([]);
      setLookupCandidates([]);
      fetchedLookupDimensions.current.clear();
    },
    [seedLookups, seedCalculated]
  );
  const selectSource = useCallback(
    (ref: string) => {
      typedController.current?.abort();
      setUriChecking(false);
      applySource(ref);
    },
    [applySource]
  );

  const setRole = useCallback((columnName: string, role: ColumnRole) => setRoles((prev) => ({ ...prev, [columnName]: role })), []);
  const setLabel = useCallback((columnName: string, label: string) => setLabels((prev) => ({ ...prev, [columnName]: label })), []);
  const setHierarchyChoice = useCallback(
    (columnName: string, choice: HierarchyChoice) => setHierarchyChoices((prev) => ({ ...prev, [columnName]: choice })),
    []
  );

  // The P&L fact's defaults pre-fill what the user has not set; its job filter applies only to an unfiltered source.
  const applyPreset = useCallback((preset: DatasetPreset) => {
    setCapabilities((prev) => ({
      ...prev,
      fx_rates: prev.fx_rates ?? preset.fx_rates,
      deck_blends: prev.deck_blends ?? { deck_types: [], scenario_map: preset.scenario_map }
    }));
    setFixedFilters((prev) => (prev.length ? prev : preset.fixed_filters));
  }, []);

  // Each time a table is chosen (a fresh read or the cache after switching back), its preset fills what is unset --
  // once per choice, so clearing the preset's filter (which re-shows the same table) does not bring it back.
  useEffect(() => {
    if (editing || !profile || presetAppliedFor.current === profile.ref) return;
    presetAppliedFor.current = profile.ref;
    if (profile.preset) applyPreset(profile.preset);
  }, [editing, profile, applyPreset]);

  const runProfile = useCallback(
    (ref: string, role?: string) => {
      const key = profileKey(ref, appliedFilters, role);
      const cached = profiles.current.get(key);
      if (cached) {
        setProfile(cached);
        return;
      }
      if (profileInFlight.current === key) return;
      profileController.current?.abort();
      const controller = new AbortController();
      profileController.current = controller;
      profileInFlight.current = key;
      setError(null);
      setProfileLoading(true);
      profileDataset(ref, controller.signal, appliedFilters, role)
        .then((result) => {
          profiles.current.set(key, result);
          if (controller.signal.aborted) return;
          setProfile(result);
        })
        .catch((caught: unknown) => {
          if (!controller.signal.aborted) setError(errorMessage(caught));
        })
        .finally(() => {
          if (profileController.current !== controller) return;
          profileInFlight.current = null;
          setProfileLoading(false);
        });
    },
    [appliedFilters, applyPreset]
  );

  const runAccessCheck = useCallback(
    (ref: string, role?: string) => {
      stepController.current?.abort();
      const controller = new AbortController();
      stepController.current = controller;
      setError(null);
      setAccessChecking(true);
      checkDatasetAccess(ref, controller.signal, role)
        .then((result) => {
          if (controller.signal.aborted) return;
          setAccess(result);
          // Profiling starts while the user reads the access result, so Map Columns is usually ready.
          if (result.accessible) runProfile(ref, role);
        })
        .catch((caught: unknown) => {
          if (!controller.signal.aborted) setError(errorMessage(caught));
        })
        .finally(() => {
          if (!controller.signal.aborted) setAccessChecking(false);
        });
    },
    [runProfile]
  );

  const setUriDraft = useCallback((value: string) => {
    setUriDraftValue(value);
    setUriError(null);
  }, []);
  const setRoleDraft = useCallback((value: string) => {
    setRoleDraftValue(value);
    setUriError(null);
  }, []);
  // A typed path is checked as it is chosen: a refusal (a malformed path or role, an unreadable folder)
  // stays beside the input, and an answer selects it with the access result the next step shows.
  const checkTypedSource = useCallback(
    (uri: string, role: string) => {
      typedController.current?.abort();
      const controller = new AbortController();
      typedController.current = controller;
      const trimmedRole = role.trim();
      setUriError(null);
      setUriChecking(true);
      checkDatasetAccess(uri, controller.signal, trimmedRole || undefined)
        .then((result) => {
          if (controller.signal.aborted) return;
          // Profiling waits for Map Columns, where the new source's own (empty) fixed filters apply.
          applySource(uri, trimmedRole);
          setAccess(result);
        })
        .catch((caught: unknown) => {
          if (!controller.signal.aborted) setUriError(errorMessage(caught));
        })
        .finally(() => {
          if (typedController.current === controller) setUriChecking(false);
        });
    },
    [applySource]
  );

  const scenarioColumn = useMemo(
    () => (profile?.columns ?? []).find((column) => effectiveRole(column, roles) === 'scenario')?.name,
    [profile, roles]
  );
  const timeColumn = useMemo(() => (profile?.columns ?? []).find((column) => effectiveRole(column, roles) === 'time'), [profile, roles]);
  const scenarioTime = useMemo(() => ({ column: timeColumn?.name, format: timeColumn?.time_format }), [timeColumn]);
  const scenarioSuggestions = useScenarioSuggestions(selectedRef, scenarioColumn, scenarioTime, appliedFilters, sourceRole, reachedMap);
  const { suggestions: suggestedScenarios } = scenarioSuggestions;
  // The map starts as the suggestions, for the user to review; a stored or edited map is kept.
  useEffect(() => {
    if (!suggestedScenarios.length) return;
    setCapabilities((prev) =>
      prev.deck_blends && Object.keys(prev.deck_blends.scenario_map).length
        ? prev
        : { ...prev, deck_blends: { deck_types: [], scenario_map: suggestedScenarioMap(suggestedScenarios) } }
    );
  }, [suggestedScenarios]);
  const scenarioMap = capabilities.deck_blends?.scenario_map;
  const scenarioRowList = useMemo(
    () => scenarioRows(scenarioSuggestions.values, scenarioSuggestions.months, suggestedScenarios, scenarioMap ?? {}),
    [scenarioSuggestions.values, scenarioSuggestions.months, suggestedScenarios, scenarioMap]
  );
  const mapScenarioRow = useCallback(
    (row: ScenarioRow, reporting: readonly string[]) =>
      setCapabilities((prev) => ({
        ...prev,
        deck_blends: { deck_types: prev.deck_blends?.deck_types ?? [], scenario_map: mapRow(prev.deck_blends?.scenario_map ?? {}, row, reporting) }
      })),
    []
  );

  const dimensionNames = useMemo(
    () => (profile?.columns ?? []).filter((column) => effectiveRole(column, roles) === 'dimension').map((column) => column.name),
    [profile, roles]
  );

  // Map Columns shows roles, hierarchies and the deck-blend options together; loaded once it is first reached.
  const hierarchiesFetcher = useCallback(
    async (signal: AbortSignal): Promise<DatasetColumnHierarchy[]> =>
      reachedMap ? (await listHierarchies(signal).catch(() => ({ hierarchies: [] }))).hierarchies ?? [] : [],
    [reachedMap]
  );
  const hierarchies = useDefinitionResource(hierarchiesFetcher);
  const reportingFetcher = useCallback(
    async (signal: AbortSignal): Promise<string[]> => (reachedMap ? getReportingScenarios(signal) : []),
    [reachedMap]
  );
  const reporting = useDefinitionResource(reportingFetcher);

  // Ask only for dimensions not fetched yet, so a column re-typed to a dimension after the first load
  // still gets suggestions; results append since the fresh columns are disjoint from the fetched set.
  const loadLookupCandidates = useCallback(
    (ref: string, columns: readonly string[]) => {
      const fresh = columns.filter((column) => !fetchedLookupDimensions.current.has(column));
      if (fresh.length === 0) return;
      fresh.forEach((column) => fetchedLookupDimensions.current.add(column));
      suggestLookupCandidates(ref, fresh, undefined, appliedFilters, sourceRole)
        .then((response) => setLookupCandidates((prev) => [...prev, ...response.candidates]))
        .catch(() => fresh.forEach((column) => fetchedLookupDimensions.current.delete(column)));
    },
    [appliedFilters, sourceRole]
  );

  const gate = useMemo(
    () => ({ selectedRef, access, profile, roles, hierarchyChoices, name, fixedFilters }),
    [selectedRef, access, profile, roles, hierarchyChoices, name, fixedFilters]
  );

  // The source step waits on its table list and the chosen table's columns, or Next races a load.
  // A typed path counts only once the path and role on screen are the ones that were checked.
  const typedPending = sourceMode === 'uri' && (uriChecking || uriDraft.trim() !== (selectedRef ?? '') || roleDraft.trim() !== roleArn.trim());
  const sourceBusy = step === 'source' && (sources.loading || sourceColumns.loading || typedPending);

  const next = useCallback(() => {
    if (sourceBusy || !canAdvance(step, gate)) return;
    const index = stepIndex(step, steps);
    if (isLastStep(step, steps)) return;
    const nextStep = steps[index + 1];
    // A source already found readable is not checked again.
    if (nextStep === 'access' && selectedRef && !access?.accessible) runAccessCheck(selectedRef, sourceRole);
    if (nextStep === 'map' && selectedRef && !profile) runProfile(selectedRef, sourceRole);
    setStep(nextStep);
  }, [sourceBusy, step, steps, gate, selectedRef, access, profile, runAccessCheck, runProfile, sourceRole]);

  // Map Columns never waits on a profile nobody is fetching: one dropped (a filter edit, a stale
  // response) is read again, from the cache when it can be.
  useEffect(() => {
    if (step === 'map' && selectedRef && !profile && !profileLoading && !error) runProfile(selectedRef, sourceRole);
  }, [step, selectedRef, profile, profileLoading, error, runProfile, sourceRole]);

  useEffect(() => {
    if (step === 'map') setReachedMap(true);
  }, [step]);

  useEffect(() => {
    if (step === 'map' && profile && selectedRef) loadLookupCandidates(selectedRef, dimensionNames);
  }, [step, profile, selectedRef, dimensionNames, loadLookupCandidates]);

  const back = useCallback(() => {
    const index = stepIndex(step, steps);
    if (index > 0) setStep(steps[index - 1]);
  }, [step, steps]);

  const mapping = useMemo(() => ({ roles, labels, hierarchyChoices, measureDimension }), [roles, labels, hierarchyChoices, measureDimension]);
  // Everything but the name, so typing the name does not re-run (and re-lay out) the Review query.
  const draft = useMemo(
    () =>
      profile
        ? {
            ...buildDatasetDescription('', profile, mapping, lookups, fixedFilters, capabilities, calculated.measures),
            ...(sourceRole ? { role_arn: sourceRole } : {})
          }
        : null,
    [profile, mapping, lookups, fixedFilters, capabilities, calculated.measures, sourceRole]
  );
  const description = useMemo(() => (draft ? { ...draft, name: name.trim() } : null), [draft, name]);

  // Review shows the query and a sample before save, from the draft itself; re-read (debounced) as the
  // draft changes. Once saved, the saved dataset's own preview replaces it.
  useEffect(() => {
    if (step !== 'review' || saved || !profile || !draft) return undefined;
    const request = reviewPreviewRequest(profile, mapping, calculated.measures);
    if (!request) {
      setReviewPreview(null);
      setReviewPreviewError(null);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setReviewPreviewLoading(true);
      setReviewPreviewError(null);
      previewDraftDataset(draft, request, controller.signal)
        .then((response) => setReviewPreview(response))
        .catch((caught: unknown) => {
          if (!controller.signal.aborted) setReviewPreviewError(errorMessage(caught));
        })
        .finally(() => {
          if (!controller.signal.aborted) setReviewPreviewLoading(false);
        });
    }, DRAFT_PREVIEW_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [step, saved, profile, mapping, draft, calculated.measures]);

  const save = useCallback(() => {
    if (!description || !canAdvance('review', gate)) return;
    stepController.current?.abort();
    const controller = new AbortController();
    stepController.current = controller;
    setError(null);
    setSaving(true);
    const request = editingId ? updateDataset(editingId, description, controller.signal) : createDataset(description, controller.signal);
    request
      .then((dataset) => {
        if (controller.signal.aborted) return;
        setSaved(dataset);
        onSaved?.(dataset);
      })
      .catch((caught: unknown) => {
        if (!controller.signal.aborted) setError(errorMessage(caught));
      })
      .finally(() => {
        if (!controller.signal.aborted) setSaving(false);
      });
  }, [description, gate, editingId, onSaved]);

  const publish = useCallback(() => {
    if (!saved || publishing || published) return;
    const controller = new AbortController();
    publishController.current = controller;
    setPublishing(true);
    setError(null);
    publishDataset(saved.id, controller.signal)
      .then(() => {
        if (controller.signal.aborted) return;
        setPublished(true);
        onSaved?.({ ...saved, status: 'published' });
      })
      .catch((caught: unknown) => {
        if (!controller.signal.aborted) setError(errorMessage(caught));
      })
      .finally(() => {
        if (!controller.signal.aborted) setPublishing(false);
      });
  }, [saved, publishing, published, onSaved]);

  const pendingCandidates = useMemo(() => lookupSelection.pending(lookupCandidates), [lookupSelection, lookupCandidates]);

  return {
    editing,
    readOnlyRef: editing ? selectedRef : null,
    steps,
    step,
    search,
    setSearch,
    sourcePage,
    setSourcePage,
    sources: sources.data ?? [],
    sourcesLoading: sources.loading && !sources.data,
    refreshingSources: sourceRefreshes > 0 && sources.loading,
    refreshSources,
    selectedRef,
    selectSource,
    sourceMode,
    setSourceMode,
    uriDraft,
    setUriDraft,
    roleDraft,
    setRoleDraft,
    uriError,
    uriChecking,
    checkTypedSource,
    roleArn: sourceRole,
    sourceColumns: sourceColumns.data ?? [],
    sourceColumnsLoading: sourceColumns.loading,
    fixedFilters,
    appliedFixedFilters: appliedFilters,
    addFixedFilter,
    updateFixedFilter,
    removeFixedFilter,
    access,
    accessChecking,
    profile,
    profileLoading,
    roles,
    setRole,
    labels,
    setLabel,
    hierarchyChoices,
    setHierarchyChoice,
    allHierarchies: hierarchies.data ?? [],
    hierarchiesLoading: reachedMap && hierarchies.loading,
    measureDimension,
    setMeasureDimension,
    capabilities,
    setCapabilities,
    calculatedMeasures: calculated.measures,
    addCalculatedMeasure: calculated.add,
    updateCalculatedMeasure: calculated.update,
    removeCalculatedMeasure: calculated.remove,
    scenarioColumn,
    scenarioPeriod: scenarioSuggestions.period,
    scenarioRows: scenarioRowList,
    mapScenarioRow,
    scenarioSuggestionsLoading: scenarioSuggestions.loading,
    scenarioSuggestionsError: scenarioSuggestions.error,
    presetReason: profile?.preset?.reason ?? null,
    reportingScenarios: reporting.data ?? [],
    reportingScenariosError: reporting.error?.message ?? null,
    reloadReportingScenarios: reporting.reload,
    lookups,
    lookupChecks: lookupSelection.checks,
    pendingCandidates,
    acceptCandidate: lookupSelection.accept,
    upsertLookup: lookupSelection.upsert,
    removeLookup: lookupSelection.remove,
    name,
    setName,
    canAdvance: !sourceBusy && canAdvance(step, gate),
    next,
    back,
    saving,
    error: error ?? sources.error?.message ?? null,
    saved,
    save,
    reviewPreview,
    reviewPreviewLoading,
    reviewPreviewError,
    publishing,
    published,
    publish
  };
}

function storedCalculatedMeasure(column: {
  name: string;
  formula?: string;
  per_row?: boolean;
  format?: CalculatedMeasure['format'];
}): CalculatedMeasure {
  return { name: column.name, formula: column.formula ?? '', per_row: Boolean(column.per_row), format: column.format ?? 'currency' };
}

function profileKey(ref: string, filters: readonly DatasetFixedFilter[], role?: string): string {
  return `${ref}\u0000${JSON.stringify(filters)}\u0000${role ?? ''}`;
}

export type DatasetWizardState = ReturnType<typeof useDatasetWizard>;
